import * as React from 'react';
import { bindActionCreators } from 'redux';
import { useSelector, useDispatch } from 'react-redux';

import { CoreSelectProps } from '../types/props';

import { Nullable } from '@common/typescript/objects/Nullable';

import {
	ActionCreatorProps,
	getActionCreators,
	IActionCreators,
	SelectRequestParams,
} from '@app/store/SelectList/ListActions';
import { GeneralKey, SelectList } from '@app/store/SelectList/SelectList';
import { SelectRequestProps } from '@app/store/SelectList/SelectsInterfaces';
import { selectListRequest } from '@app/store/SelectList/SelectListRequests';
import { ApplicationState } from '@app/store';
import { FilterType, StoreType } from '@app/store/SelectList/UtilityTypes';

export function usePreselect<
	TValue,
	TStore extends keyof SelectList,
	TParams extends SelectRequestParams<TValue>,
>(props: CoreSelectProps<TValue, TStore, TParams>): void {
	const reqProps: SelectRequestProps<TStore> = selectListRequest[props.store];
	const store: StoreType<TStore> = useSelector<ApplicationState, StoreType<TStore>>((state: ApplicationState) =>
		state.selects[props.store]);

	const dispatch = useDispatch();
	const factory: IActionCreators<TValue, TStore> = React.useMemo(
		() => {
			const creatorProps: ActionCreatorProps<TStore> = {
				endpoint: reqProps.endpoint,
				key: props.storeKey ?? GeneralKey,
				isEqual: reqProps.isEqual,
			};

			return bindActionCreators(getActionCreators<TValue, TStore>(props.store, creatorProps), dispatch);
		},
		[dispatch, props.storeKey],
	);

	React.useEffect(() => {
		let value = props.value;
		if (!reqProps.isEmpty(store.filters as Nullable<FilterType<TStore>>) || props.noPreselect) {
			value = undefined;
		}

		if (reqProps.isValid && !reqProps.isValid(props.filters)) return;
		if (props.shouldFetch === false) return;

		const preselect: Array<TValue> = Array.isArray(value) ? value : [];
		if (!Array.isArray(value) && value !== undefined && value !== null) {
			preselect.push(value);
		}

		factory.request(props.filters, { ...props.reqParams, preselect });
	}, [factory, props.filters, props.reqParams, props.shouldFetch]);
}
